import { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  CardHeaderWidget,
  BreadCrumbs,
  SnackBarsComponent,
  SaveAction,
} from "../../components";
import { Card, CardContent, CircularProgress, Snackbar } from "@mui/material";

import { AxiosContext } from "../../context/AxiosContext";
import { makeStyles } from "@mui/styles";
import viewsTexts from "../../common/viewsTexts.json";
import { alphabeticalSort } from "../../helpers";
import SelectMultipleWidgetFilter from "components/SelectMultipleWidgetFilter";
import { alterArrayAddLabels } from "helpers/functionHelpers";
import { BsCol } from "layouts/components/newLayouts";
import AutoCompleteFreeSoloController from "components/Search/AutoCompleteFreeSoloController";
import {
  getClientObj,
  getOrchardObj,
  getCcObj,
  clearSelects,
  getQuarterObj,
} from "./helpers";
import { AgGridReact } from "@ag-grid-community/react";
import { AG_GRID_LOCALE_ES } from "@ag-grid-community/locale";

const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.tertiary.main,
    fontWeight: 500,
  },
  divider: {
    background: theme.palette.grey[400],
    marginTop: 10,
  },
  rootHelper: {
    fontSize: 17,
  },
  inputText: {
    fontSize: 26,
  },
}));

const AuditLibrary = (props) => {
  const axiosContext = useContext(AxiosContext);

  const [loadingFilterState, setLoadingFilterState] = useState(false);
  const [loadingQueryState, setLoadingQueryState] = useState(false);
  // const [successQueryState, setSuccessQueryState] = useState(false);
  const [clientDataState, setClientDataState] = useState([]);
  const [orchardOptions, setOrchardOptions] = useState([]);
  const [ccDataState, setCcDataState] = useState([]);
  const [quarterDataState, setQuarterDataState] = useState([]);
  const [seasonDataState, setSeasonDataState] = useState([]);
  const [columnDataState, setColumnDataState] = useState([]);
  const [filterDataState, setFilterDataState] = useState([]);

  // Ag grid
  const [visibleColumnsState, setVisibleColumnsState] = useState([]);
  const [tableStyleState, setTableStyleState] = useState({
    height: 700,
  });
  const paginationPageSizeSelector = [20, 50, 100, 500, 1000];
  const localeText = AG_GRID_LOCALE_ES;
  const [rowData, setRowData] = useState([]);

  const [errorState, setErrorState] = useState({
    error: false,
    message: "",
  });

  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  // Helpers para extraer todas las opciones posibles
  const getAllOrchards = (dataArr) => {
    let orchards = [];
    dataArr.forEach((client) => {
      if (client.orchardValue) {
        client.orchardValue.forEach((orch) => {
          orchards.push({
            label: orch.orchard,
            value: orch.orchardValue,
            id: orch.orchardValue,
          });
        });
      }
    });
    return orchards;
  };

  const watchClient = watch("client");
  const watchOrchard = watch("orchard");
  const watchCc = watch("cc");
  const watchQuarter = watch("quarter");

  //* -------------------------
  //*     USE EFFECTS
  //*  ------------------------ *//
  useEffect(() => {
    getDataAsync();
  }, []);

  const getDataAsync = useCallback(async () => {
    setLoadingFilterState(true);
    try {
      let response = await axiosContext.authAxios.get(
        "/api-task/report/audit-yield-filter"
      );

      const { data, status } = response;
      if (status === 200) {
        let dataClient = alphabeticalSort(
          data.result["clientValue"] || [],
          "client"
        )
          .filter((e) => e.clientValue)
          .map((ele) => ({
            label: ele.client,
            value: ele.clientValue,
            id: ele.clientValue,
          }));
        setValue("client", null);
        setClientDataState(dataClient);
        setOrchardOptions(getAllOrchards(data.result["clientValue"] || []));
        setSeasonDataState(formatSeason(data.result["season"] || []));

        setColumnDataState(
          data.result["columns"].map((column) => ({
            label: column.value,
            value: column.value,
            id: column.id,
          }))
        );
        setFilterDataState(data.result);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingFilterState(false);
    }
  }, [axiosContext, setLoadingFilterState]);

  const onSubmit = async (values) => {
    setLoadingQueryState(true);
    let query = {};
    const fieldsToFilter = [
      { key: "orchard", queryKey: "orchard" },
      { key: "client", queryKey: "clientValue" },
      { key: "cc", queryKey: "ccValue" },
      { key: "quarter", queryKey: "quarterValue" },
      { key: "season", queryKey: "season" },
    ];

    fieldsToFilter.forEach(({ key, queryKey }) => {
      if (values[key] && values[key].value) {
        query[queryKey] = values[key].value;
      }
    });
    if (values.column) {
      query.column = values.column;
    }

    try {
      const response = await axiosContext.authAxios.post(
        "/api-task/report/audit-yield",
        query
      );
      if (response?.status === 200) {
        if (response?.data?.status === 400) {
          setErrorState({
            error: true,
            message: response.data.error || "Error al cargar los datos",
          });
          return;
        }

        setVisibleColumnsState(finalColumns);
        const rowDataFullTransform = processDataRowDetailFull(
          response.data.result.sucess || []
        );

        setRowData(rowDataFullTransform);

        reset({
          client: null,
          orchard: null,
          cc: null,
          quarter: null,
          season: null,
          column: null,
        });
      }

      setLoadingQueryState(false);
    } catch (err) {
      console.log("Error", err);
      setErrorState({ error: true, message: "Error al cargar los datos" });
      setLoadingQueryState(false);
    } finally {
      setLoadingQueryState(false);
    }
  };

  const processDataRowDetailFull = (data) => {
    const rows = [];

    data.forEach((item) => {
      const row = {
        id: `${item.season}_${item.idUnitProductive}_${Math.random()}`,
        idUnitProductive: item.idUnitProductive,
        season: item?.season,
        client: item.client,
        orchard: item.orchard,
        cc: item.cc,
        quarter: item.quarter,
        specie: item.specie,
        variety: item.variety,
        calculated: item.calculated,
        column: item.column,
        dependencies: item.dependencies,
        formula: item.formula,
        rules: item.rules,
        reason: item.reason || "",
      };
      rows.push(row);
    });

    return rows;
  };

  //* -------------------------
  //*     USE EFFECTS
  //*  ------------------------ *//

  useEffect(() => {
    if (watchClient && watchClient.value && filterDataState.clientValue) {
      clearSelects(setValue, ["orchard", "cc", "quarter", "season"]);
      const clientObj = getClientObj(
        filterDataState.clientValue,
        watchClient.value
      );
      setOrchardOptions(
        alterArrayAddLabels(clientObj?.orchardValue || [], [
          "orchardValue",
          "orchard",
        ])
      );
    } else {
      clearSelects(setValue, ["orchard"]);
      setOrchardOptions([]);
    }
  }, [watchClient]);

  // Filtrado dinámico de CC según huerto seleccionado
  useEffect(() => {
    if (watchClient && watchClient.value && filterDataState.clientValue) {
      const clientObj = getClientObj(
        filterDataState.clientValue,
        watchClient.value
      );
      if (clientObj && clientObj.orchardValue) {
        if (watchOrchard && watchOrchard.value) {
          clearSelects(setValue, ["cc"]);
          const orchardObj = getOrchardObj(clientObj, watchOrchard.value);
          if (orchardObj && orchardObj.ccValue) {
            setCcDataState(
              alterArrayAddLabels(orchardObj.ccValue, ["ccValue", "cc"])
            );
          } else {
            setCcDataState([]);
          }
        } else {
          clearSelects(setValue, ["cc"]);
          setCcDataState([]);
        }
      } else {
        clearSelects(setValue, ["cc"]);
        setCcDataState([]);
      }
    } else {
      clearSelects(setValue, ["cc"]);
      setCcDataState([]);
    }
  }, [watchClient, watchOrchard, filterDataState]);

  // Filtrado dinámico de Cuartel según CC seleccionado
  useEffect(() => {
    if (
      watchClient &&
      watchClient.value &&
      filterDataState.clientValue &&
      watchOrchard &&
      watchOrchard.value
    ) {
      const clientObj = getClientObj(
        filterDataState.clientValue,
        watchClient.value
      );
      if (clientObj && clientObj.orchardValue) {
        const orchardObj = getOrchardObj(clientObj, watchOrchard.value);
        if (orchardObj && orchardObj.ccValue) {
          if (watchCc && watchCc.value) {
            clearSelects(setValue, ["quarter"]);
            const ccObj = getCcObj(orchardObj, watchCc.value);
            if (ccObj && ccObj.quarterValue) {
              setQuarterDataState(
                alterArrayAddLabels(ccObj.quarterValue, [
                  "quarterValue",
                  "quarter",
                ])
              );
            } else {
              clearSelects(setValue, ["quarter"]);
              setQuarterDataState([]);
            }
          } else {
            clearSelects(setValue, ["quarter"]);
            setQuarterDataState([]);
          }
        } else {
          clearSelects(setValue, ["quarter"]);
          setQuarterDataState([]);
        }
      } else {
        clearSelects(setValue, ["quarter"]);
        setQuarterDataState([]);
      }
    } else {
      clearSelects(setValue, ["quarter"]);
      setQuarterDataState([]);
    }
  }, [watchClient, watchOrchard, watchCc, filterDataState]);

  useEffect(() => {
    if (watchClient && watchClient.value && filterDataState.clientValue) {
      const clientObj = getClientObj(
        filterDataState.clientValue,
        watchClient.value
      );
      setSeasonDataState(formatSeason(clientObj.season || []));
      if (watchOrchard && watchOrchard.value) {
        if (clientObj && clientObj.orchardValue) {
          const orchardObj = getOrchardObj(clientObj, watchOrchard.value);
          if (orchardObj && orchardObj.ccValue) {
            setSeasonDataState(formatSeason(orchardObj.season));
            if (watchCc && watchCc.value) {
              clearSelects(setValue, ["season"]);
              const ccObj = getCcObj(orchardObj, watchCc.value);
              if (ccObj && ccObj.season) {
                setSeasonDataState(formatSeason(ccObj.season));
                if (watchQuarter && watchQuarter.value) {
                  const quarterObj = getQuarterObj(ccObj, watchQuarter.value);
                  if (quarterObj && quarterObj.season) {
                    setSeasonDataState(formatSeason(quarterObj.season || []));
                  }
                } else {
                  clearSelects(setValue, ["season"]);
                  setSeasonDataState(formatSeason(ccObj.season || []));
                }
              } else {
                clearSelects(setValue, ["season"]);
                setSeasonDataState(formatSeason(orchardObj.season || []));
              }
            } else {
              // Si no hay cc seleccionado, mostrar todas las temporadas de todos los cc del huerto
              clearSelects(setValue, ["season"]);
              setSeasonDataState(formatSeason(orchardObj.season || []));
            }
          } else {
            clearSelects(setValue, ["season"]);
            setSeasonDataState(formatSeason(filterDataState.season || []));
          }
        } else {
          clearSelects(setValue, ["season"]);
          setSeasonDataState(formatSeason(filterDataState.season || []));
        }
      }
    } else {
      clearSelects(setValue, ["season"]);
      filterDataState &&
        setSeasonDataState(formatSeason(filterDataState?.season || []));
    }
  }, [watchClient, watchOrchard, watchCc, watchQuarter, filterDataState]);

  //* -------------------------
  //*     UTILITIES
  //*  ------------------------ *//
  //? SIDE BAR AG GRID
  const sideBar = {
    toolPanels: [
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: {
          suppressPivotMode: true, // desactivar el modo pivot
          suppressRowGroups: false, // desactivar la agrupación de filas
          suppressValues: true, // desactivar los valores
          suppressColumnFilter: false, // desactivar el filtro de busqueda en las columnas
          contractColumnSelection: true, // contraer la selección de columnas al incio
          suppressColumnSelectAll: false, // desactivar el check de selección de todas las columnas
        },
        minWidth: 225,
        maxWidth: 225,
        width: 225,
      },
    ],
    position: "left",
  };

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitCellContents",
    };
  }, []);

  const handleCloseSnackBarsComponent = () => {
    setErrorState({
      error: false,
      message: "",
    });
  };

  const formatSeason = (arra) => {
    return arra.map((season) => ({
      label: season,
      value: season,
    }));
  };

  //* -------------------------
  //*     AG GRID
  //*  ------------------------ *//
  const finalColumns = [
    {
      colId: "caracter",
      headerName: "Caracterización",
      children: [
        {
          headerName: "ID Unidad Productiva",
          field: "idUnitProductive",
          editable: false,
          suppressFillHandle: true,
          filter: "agMultiColumnFilter",
          suppressMovable: true,
          enableRowGroup: true,
          filterParams: {
            filters: [
              {
                filter: "agTextColumnFilter",
                filterParams: {
                  defaultOption: "contains",
                  filterOptions: ["contains"],
                },
              },
              {
                filter: "agSetColumnFilter",
                filterParams: {
                  suppressMiniFilter: true,
                },
              },
            ],
          },
        },
        {
          headerName: "Temporada",
          field: "season",
          editable: false,
          suppressFillHandle: true,
          filter: "agMultiColumnFilter",
          suppressMovable: true,
          enableRowGroup: true,
          filterParams: {
            filters: [
              {
                filter: "agTextColumnFilter",
                filterParams: {
                  defaultOption: "contains",
                  filterOptions: ["contains"],
                },
              },
              {
                filter: "agSetColumnFilter",
                filterParams: {
                  suppressMiniFilter: true,
                },
              },
            ],
          },
        },
        {
          headerName: "Cliente",
          field: "client",
          editable: false,
          suppressFillHandle: true,
          filter: "agMultiColumnFilter",
          suppressMovable: true,
          enableRowGroup: true,
          filterParams: {
            filters: [
              {
                filter: "agTextColumnFilter",
                filterParams: {
                  defaultOption: "contains",
                  filterOptions: ["contains"],
                },
              },
              {
                filter: "agSetColumnFilter",
                filterParams: {
                  suppressMiniFilter: true,
                },
              },
            ],
          },
        },
        {
          headerName: "Huerto",
          field: "orchard",
          editable: false,
          suppressFillHandle: true,
          filter: "agMultiColumnFilter",
          suppressMovable: true,
          enableRowGroup: true,
          hide: false,
          filterParams: {
            filters: [
              {
                filter: "agTextColumnFilter",
                filterParams: {
                  defaultOption: "contains",
                  filterOptions: ["contains"],
                },
              },
              {
                filter: "agSetColumnFilter",
                filterParams: {
                  suppressMiniFilter: true,
                },
              },
            ],
          },
        },
        {
          headerName: "CC",
          field: "cc",
          editable: false,
          suppressFillHandle: true,
          filter: "agMultiColumnFilter",
          suppressMovable: true,
          enableRowGroup: true,
          filterParams: {
            filters: [
              {
                filter: "agTextColumnFilter",
                filterParams: {
                  defaultOption: "contains",
                  filterOptions: ["contains"],
                },
              },
              {
                filter: "agSetColumnFilter",
                filterParams: {
                  suppressMiniFilter: true,
                },
              },
            ],
          },
        },
        {
          headerName: "Cuartel",
          field: "quarter",
          editable: false,
          suppressFillHandle: true,
          filter: "agMultiColumnFilter",
          suppressMovable: true,
          enableRowGroup: true,
          filterParams: {
            filters: [
              {
                filter: "agTextColumnFilter",
                filterParams: {
                  defaultOption: "contains",
                  filterOptions: ["contains"],
                },
              },
              {
                filter: "agSetColumnFilter",
                filterParams: {
                  suppressMiniFilter: true,
                },
              },
            ],
          },
        },
        {
          headerName: "Especie",
          field: "specie",
          editable: false,
          suppressFillHandle: true,
          filter: "agMultiColumnFilter",
          suppressMovable: true,
          enableRowGroup: true,
          filterParams: {
            filters: [
              {
                filter: "agTextColumnFilter",
                filterParams: {
                  defaultOption: "contains",
                  filterOptions: ["contains"],
                },
              },
              {
                filter: "agSetColumnFilter",
                filterParams: {
                  suppressMiniFilter: true,
                },
              },
            ],
          },
        },
        {
          headerName: "Variedad",
          field: "variety",
          editable: false,
          filter: "agMultiColumnFilter",
          suppressMovable: true,
          enableRowGroup: true,
          filterParams: {
            filters: [
              {
                filter: "agTextColumnFilter",
                filterParams: {
                  defaultOption: "contains",
                  filterOptions: ["contains"],
                },
              },
              {
                filter: "agSetColumnFilter",
                filterParams: {
                  suppressMiniFilter: true,
                },
              },
            ],
          },
          suppressFillHandle: true,
        },
      ],
    },
    // Columnas libres fuera de caracterización
    {
      headerName: "Calculated",
      field: "calculated",
      editable: false,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Reason",
      field: "reason",
      editable: false,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Columna",
      field: "column",
      editable: false,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Dependencias",
      field: "dependencies",
      editable: false,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      valueFormatter: (params) =>
        Array.isArray(params.value) ? params.value.join(", ") : params.value,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Fórmula",
      field: "formula",
      editable: false,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
    {
      headerName: "Reglas",
      field: "rules",
      editable: false,
      filter: "agMultiColumnFilter",
      suppressMovable: true,
      enableRowGroup: true,
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              filterOptions: ["contains"],
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              suppressMiniFilter: true,
            },
          },
        ],
      },
    },
  ];

  return (
    <div className="form-style">
      <div className="container mx-auto px-10">
        <Card elevation={0}>
          <CardHeaderWidget
            title={viewsTexts.titles.auditLibrary.title}
            subheader={<BreadCrumbs subTitle="" />}
          />
          <CardContent>
            <form
              autoComplete="on"
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              <BsCol>
                <SelectMultipleWidgetFilter
                  options={clientDataState}
                  titleOption="label"
                  control={control}
                  name="client"
                  labeltext="Cliente"
                  errors={errors.client}
                  defaultValue=""
                  loading={loadingFilterState}
                  req
                  withDefault
                  variant="outlined"
                  placeholder="Seleccione Cliente"
                  display="vertical"
                  minheight={false}
                  isClearable={true}
                  isSearchable
                  sizeSelect={"large"}
                  isMulti={false}
                />
                <SelectMultipleWidgetFilter
                  options={orchardOptions}
                  titleOption="label"
                  control={control}
                  name="orchard"
                  labeltext="Huerto"
                  errors={errors.orchard}
                  defaultValue=""
                  loading={loadingFilterState}
                  withDefault
                  variant="outlined"
                  placeholder="Seleccione Huerto"
                  display="vertical"
                  minheight={false}
                  isClearable={true}
                  isSearchable
                  sizeSelect={"large"}
                  isMulti={false}
                />
              </BsCol>
              <BsCol>
                <SelectMultipleWidgetFilter
                  options={ccDataState}
                  titleOption="label"
                  control={control}
                  name="cc"
                  labeltext="Centro de Costo"
                  errors={errors.cc}
                  multiple={true}
                  defaultValue={[]}
                  loading={loadingFilterState}
                  withDefault
                  variant="outlined"
                  placeholder="Seleccione cc"
                  display="vertical"
                  minheight={false}
                  isClearable={true}
                  isSearchable
                  sizeSelect={"large"}
                  isMulti={false}
                />
                <SelectMultipleWidgetFilter
                  options={quarterDataState}
                  titleOption="label"
                  control={control}
                  name="quarter"
                  labeltext="Cuartel"
                  errors={errors.quarter}
                  multiple={true}
                  defaultValue={[]}
                  loading={loadingFilterState}
                  withDefault
                  variant="outlined"
                  placeholder="Seleccione el Cuartel"
                  display="vertical"
                  minheight={false}
                  isClearable={true}
                  isSearchable
                  sizeSelect={"large"}
                  isMulti={false}
                />
                <SelectMultipleWidgetFilter
                  options={seasonDataState}
                  titleOption="label"
                  control={control}
                  name="season"
                  labeltext="Temporada"
                  errors={errors.season}
                  multiple={true}
                  defaultValue={[]}
                  loading={loadingFilterState}
                  withDefault
                  variant="outlined"
                  placeholder="Seleccione Temporada"
                  display="vertical"
                  minheight={false}
                  isClearable={true}
                  isSearchable
                  sizeSelect={"large"}
                  isMulti={false}
                />
              </BsCol>
              <BsCol>
                <AutoCompleteFreeSoloController
                  control={control}
                  data={columnDataState}
                  name="column"
                  labeltext="Columna"
                  errors={errors.column}
                  defaultValue=""
                  loadingComboState={loadingFilterState}
                  placeholder="Yemas/Dardo...."
                  display="vertical"
                />
              </BsCol>
              <div className="flex justify-end">
                <SaveAction
                  title="Filtrar"
                  color="primary"
                  size="large"
                  className="roundedButton"
                  variant="contained"
                  disabled={loadingQueryState}
                  messageType="add"
                  mutationLoading={loadingQueryState}
                />
              </div>
            </form>
            {errorState.error && (
              <Snackbar
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                open={errorState.error}
                onClose={() => setErrorState({ ...errorState, error: false })}
                autoHideDuration={4500}
                key={Math.random()}
              >
                <SnackBarsComponent
                  variant="error"
                  message={errorState.message}
                  handleClose={handleCloseSnackBarsComponent}
                />
              </Snackbar>
            )}
          </CardContent>
        </Card>

        {loadingQueryState ? (
          <div className="flex justify-center items-center">
            <CircularProgress />
          </div>
        ) : (
          <>
            {rowData && rowData.length > 0 ? (
              <div className="ag-theme-quartz" style={tableStyleState}>
                <AgGridReact
                  rowData={rowData || []}
                  columnDefs={visibleColumnsState}
                  pagination={true}
                  paginationPageSize={50}
                  suppressMovableColumns={true}
                  suppressDragLeaveHidesColumns={true}
                  paginationPageSizeSelector={paginationPageSizeSelector}
                  localeText={localeText}
                  defaultColDef={{
                    resizable: true,
                    autoHeight: true,
                    suppressAutoSize: true,
                    floatingFilter: true,
                    filter: false,
                  }}
                  autoSizeStrategy={autoSizeStrategy}
                  groupDisplayType={"multipleColumns"}
                  rowGroupPanelShow={"always"}
                  enableRangeSelection={true}
                  enableFillHandle={true}
                  sideBar={sideBar}
                  embedFullWidthRows={true}
                  getRowId={(params) => params.data.id}
                />
              </div>
            ) : (
              <div className="flex justify-center items-center">
                <h3 className=" text-gray-400 text-lg mt-20">
                  {" "}
                  No se encontraron resultados. Ajusta los filtros o realiza una
                  nueva búsqueda.
                </h3>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default AuditLibrary;
